import React, { useState } from "react";

import StandardTable from "../../shared/bootstrap/TableWrapper.jsx";
import TableHeader from "./TableHeader.jsx";
import TableEntry from "./TableEntry.jsx";

import { SORT_COMPANY_ASC, SEARCH_TYPE_NAME, SEARCH_TYPE_ALL } from "../../../constants.js";
import { contractHolderSortMap } from "../../../util/sort.js";
import { prepareTextForSearch } from "../../../util/format.js";
import { filterProgramManagers } from "../../../util/programManagers.js";

/**
 * @typedef {import("../../../jsdoc/ProgramManager.js").ProgramManager} ProgramManager
 */

/**
 * Filters program manager list based on search text
 * @param {ProgramManager[]} programManagers
 * @param {string} text
 * @param {SEARCH_TYPE_NAME | SEARCH_TYPE_ALL} searchType
 * @returns {ProgramManager[]} filtered list of program managers
 */
const filterByText = (programManagers, text, searchType) => {
  const reg = new RegExp(prepareTextForSearch(text));

  if (searchType === SEARCH_TYPE_NAME) {
    //only search fullName
    programManagers = programManagers.filter(({ fullName }) => reg.test(fullName.toLowerCase()));
  } else {
    //search everything
    programManagers = programManagers.filter(
      ({ fullName, position, firstName, lastName, email, phoneNumbers, contracts }) =>
        reg.test(fullName.toLowerCase()) ||
        reg.test(position.toLowerCase()) ||
        reg.test(firstName.toLowerCase()) ||
        reg.test(lastName.toLowerCase()) ||
        reg.test(email.toLowerCase()) ||
        contracts.some(({ sewpContractNumber }) => reg.test(sewpContractNumber.toLowerCase())) ||
        phoneNumbers.some(({ phone }) => reg.test(phone))
    );
  }

  return programManagers;
};

/**
 * Program Manager Table Component
 * @param {Object} props
 * @param {ProgramManager[]} props.programManagers
 * @param {string} props.searchValue
 * @param {SEARCH_TYPE_NAME | SEARCH_TYPE_ALL} props.searchType
 * @param {Object} props.options
 * @param {Object} props.options.groupOptions
 * @param {string} props.options.groupOptions.group
 * @param {string} props.options.groupOptions.facet
 * @param {number[]} props.options.otherOptions
 * @returns {React.JSX.Element}
 */
export default function Table({ programManagers, searchValue, searchType, options }) {
  // Init state
  const [sortOption, setSortOption] = useState(SORT_COMPANY_ASC);
  // sort programManagers data based on sort type
  let sortedFilteredProgramManagers = programManagers.sort(contractHolderSortMap[sortOption]);

  if (searchValue !== "") {
    sortedFilteredProgramManagers = filterByText(sortedFilteredProgramManagers, searchValue, searchType);
  }

  sortedFilteredProgramManagers = filterProgramManagers(sortedFilteredProgramManagers, options);

  return (
    <StandardTable
      id="programmanagerslist"
      summary="List of Program Managers for each Contract Holder"
      caption="List of Program Managers for each Contract Holder"
    >
      <thead role="rowgroup">
        <TableHeader sortOption={sortOption} setSortOption={setSortOption} />
      </thead>
      <tbody role="rowgroup">
        {sortedFilteredProgramManagers.map(
          ({
            chInfoSeq,
            sewperContactSeq,
            companyName,
            displayName,
            contracts,
            position,
            firstName,
            lastName,
            email,
            phoneNumbers,
          }) => (
            <TableEntry
              key={`${chInfoSeq}-${sewperContactSeq}-${position}`}
              chInfoSeq={chInfoSeq}
              companyName={companyName}
              displayName={displayName}
              contracts={contracts}
              position={position}
              firstName={firstName}
              lastName={lastName}
              email={email}
              phoneNumbers={phoneNumbers}
            />
          )
        )}
      </tbody>
    </StandardTable>
  );
}
